import { showToast } from '@/features/toasts/toastsSlice';
import { useWorkspace } from '@/features/workspaces/hooks';
import { useModalContext } from '@/hooks/useModalContext';
import { ToastType } from '@/models/toast';
import { useAppDispatch } from '@/reducers';
import { useDeleteDataSourceMutation } from '@/services/dataSources';
import { useDeleteAudienceMutation } from '@/services/endpoints/audiences';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UpdateListModal } from '../components/UpdateListModal';
import { ComuniList } from '../useComuniLists';
import { useImportList } from '../useImportList';
import { ImportMode } from './ImportModeDialog';
import { ListMappingModal } from './ListMappingModal';

export const useUploadedListsActions = (refetch: () => void) => {
  const { t } = useTranslation('comuni');
  const { id: workspaceId } = useWorkspace();
  const dispatch = useAppDispatch();
  const { openModal } = useModalContext();
  const openImport = useImportList(refetch);

  const [toDelete, setToDelete] = useState<ComuniList | null>(null);
  const [chooserOpen, setChooserOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteAudience] = useDeleteAudienceMutation();
  const [deleteDataSource] = useDeleteDataSourceMutation();

  const handleImportModeSelected = useCallback(
    (mode: ImportMode) => {
      setChooserOpen(false);
      if (mode === 'new') {
        openImport();
        return;
      }
      openModal({ renderContent: UpdateListModal, renderContentProps: { onDone: refetch, onUploadAsNew: openImport } });
    },
    [openImport, openModal, refetch]
  );

  const openMapping = useCallback(
    (list: ComuniList) => {
      openModal({ renderContent: ListMappingModal, renderContentProps: { list }, fullHeight: false });
    },
    [openModal]
  );

  const handleDelete = useCallback(async () => {
    if (!toDelete || isDeleting) return;

    setIsDeleting(true);
    try {
      await deleteAudience({ workspaceId, audienceId: toDelete.id }).unwrap();
      if (toDelete.isUpload) {
        for (const ds of toDelete.datasources) {
          await deleteDataSource({ workspaceId, dataSourceId: ds.id }).unwrap();
        }
      }
      dispatch(showToast({ type: ToastType.SUCCESS, title: t('lists.deleted') }));
      setToDelete(null);
      refetch();
    } catch {
      dispatch(showToast({ type: ToastType.ERROR, title: t('lists.deleteError') }));
    } finally {
      setIsDeleting(false);
    }
  }, [toDelete, isDeleting, deleteAudience, deleteDataSource, workspaceId, dispatch, t, refetch]);

  return {
    toDelete,
    setToDelete,
    chooserOpen,
    setChooserOpen,
    isDeleting,
    handleImportModeSelected,
    openMapping,
    handleDelete,
  };
};
